/**
 * PHA-1033 / PHA-1043: the data source a care-plan REFRESH starts from.
 *
 * This is the *update* flow only — the first-time onboarding wizard never consults any of
 * this. The chart used to expose the choice as two separate, unlabelled buttons
 * ("Regenerate Care Plan" = current data, "Reload patient data from stored images" =
 * re-parse the originals), which required the clinician to already know which button
 * implied which data source. Now there is one CTA and an explicit picker.
 *
 * The chosen source travels from PatientDetail to OnboardingWorkflow through
 * sessionStorage, because the two live in different routes.
 */

/**
 * 'ehr_changes' (PHA-1141) is never offered in the picker: it is the Smart Hub's "Re-generate
 * care plan" on an EHR change, which reviews the data the nightly EHR sync already pulled
 * instead of pulling again.
 */
export type CarePlanRefreshSource = 'current' | 'documents' | 'ehr' | 'upload' | 'ehr_changes';

export const REGENERATE_PATIENT_ID_STORAGE_KEY = 'regenerateCarePlanPatientId';
export const REGENERATE_SOURCE_STORAGE_KEY = 'regenerateCarePlanSource';

const ALL_SOURCES: CarePlanRefreshSource[] = ['current', 'documents', 'ehr', 'upload', 'ehr_changes'];

/**
 * Read a source back off sessionStorage. Anything unrecognised (or missing) falls back to
 * 'current' — the safest source, since it re-uses data already on the chart and imports
 * nothing.
 */
export function parseCarePlanRefreshSource(raw: string | null | undefined): CarePlanRefreshSource {
  return ALL_SOURCES.includes(raw as CarePlanRefreshSource)
    ? (raw as CarePlanRefreshSource)
    : 'current';
}

export interface CarePlanRefreshSourceOption {
  value: CarePlanRefreshSource;
  label: string;
  description: string;
}

const USE_EXISTING: CarePlanRefreshSourceOption = {
  value: 'current',
  label: 'Use existing problem list and medications',
  description:
    "Regenerate from the data already on the patient's chart, including any edits made since onboarding. Nothing is re-imported.",
};

const IMPORT_FROM_EHR: CarePlanRefreshSourceOption = {
  value: 'ehr',
  label: 'Import latest data from the EHR',
  description:
    'Pull the current problem list and medications from the EHR first, then review what changed before regenerating. The chart is matched to the EHR by MRN.',
};

const RELOAD_FROM_DOCUMENTS: CarePlanRefreshSourceOption = {
  value: 'documents',
  label: 'Reload from stored images',
  description:
    'Re-read the documents originally uploaded for this patient so you can make a new selection. Edits made since onboarding are not preserved.',
};

const UPLOAD_NEW_DOCUMENTS: CarePlanRefreshSourceOption = {
  value: 'upload',
  label: 'Upload new problem list and medications',
  description:
    'Upload fresh documents to replace stale data, then review what changed before regenerating.',
};

/**
 * The options offered for a given patient.
 *
 * EHR-integrated patients (`source='FHIR'`) have no stored documents (their lists come from
 * the FHIR feed), so they get "use what we have" vs "pull fresh from the EHR".
 *
 * PHA-1072: "does this PATIENT come from the EHR" and "can this CLINIC reach an EHR" are two
 * different questions, and the picker used to ask only the first. A patient onboarded from
 * uploaded images inside an EHR-integrated clinic was therefore never offered the EHR import,
 * even though the backend already supports it — `trigger_ehr_sync_for_patient` deliberately
 * does NOT gate on `patients.source` (a chart is keyed by MRN) and checks the ORG's
 * `ehr_provider` instead. Such a patient now gets all four sources: the two document ones it
 * already had, plus the EHR import. The other two combinations are unchanged:
 *   FHIR patient                         -> current, ehr
 *   non-FHIR, clinic WITHOUT integration -> current, documents, upload
 *
 * NOTE (deviation from PHA-1033 as written): the ticket lists only two options for the
 * non-EHR branch — "reload from stored images" or "upload new". Both of those DISCARD the
 * chart's current lists: re-parsing the originals reproduces the onboarding-day extraction
 * and drops every edit made since (PHA-1021 exists precisely because those edits matter).
 * Removing "use existing" would therefore take away a capability non-EHR clinics have
 * today, so it is kept as the first option. Flagged on PHA-1043 for Addi to confirm.
 */
export function getCarePlanRefreshSourceOptions(
  isEhrPatient: boolean,
  /**
   * PHA-1072: does the patient's CLINIC have an EHR integration configured? Defaults to
   * `isEhrPatient` so a caller that has not resolved the org flag yet gets exactly the
   * pre-PHA-1072 option set — fail closed, the EHR choice is hidden rather than wrongly shown.
   */
  orgHasEhrIntegration: boolean = isEhrPatient
): CarePlanRefreshSourceOption[] {
  if (isEhrPatient) {
    return [USE_EXISTING, IMPORT_FROM_EHR];
  }
  return [
    USE_EXISTING,
    ...(orgHasEhrIntegration ? [IMPORT_FROM_EHR] : []),
    RELOAD_FROM_DOCUMENTS,
    UPLOAD_NEW_DOCUMENTS,
  ];
}

/**
 * The step the wizard should open on for a given source.
 *
 * Every source goes through Review (step 2) — including 'current', which before PHA-1033
 * jumped straight to Approve Care Plan (step 3). That shortcut is incompatible with
 * requirement 2.1: the ✓ / ✗ / NEW markers and the "previously rejected" items live on the
 * Review step, so skipping it computes the diff and shows the clinician none of it. It also
 * puts the requirements back in their stated order — 2.1 (which problems and medications)
 * before 2.2 (which of them to focus the plan on).
 */
export function getInitialStepForRefreshSource(source: CarePlanRefreshSource): number {
  switch (source) {
    case 'upload':
      return 1; // Document Upload — the clinician supplies new files first
    case 'documents':
    case 'ehr':
    case 'ehr_changes':
    case 'current':
    default:
      return 2; // Review the problem/medication list, then continue to the care plan
  }
}

/**
 * PHA-1072 (second report): what the Document Upload step should say, and what its forward
 * button should promise, when nothing has been staged yet.
 *
 * The 'upload' refresh path enters this step with `documentsProcessed` already true — PHA-1045
 * sets it deliberately so a PARTIAL upload is allowed (a clinician who only has a new problem
 * list) and so the approve-time "at least one medication" check still has something to hold.
 * That flag is load-bearing and must NOT be unset to fix the copy.
 *
 * But the step read `documentsProcessed && nothing staged` as "this patient's documents are
 * already done", which is the wrong sentence for someone who *just chose* "Upload new problem
 * list and medications". Worse, `handleNext` keyed the same predicate and skipped straight to
 * Review — so pressing the button uploaded nothing, silently regenerated from the chart's
 * existing lists, and left every upload date untouched. Reported twice: Zack's original "the
 * 'upload new' is not providing the option to load new images", and Addi's "I'm not sure it
 * really re-uploaded as the date of upload didn't change / the message was the docs were
 * already uploaded" (staging, patient 1078).
 *
 * The forward path is deliberately NOT blocked — PHA-1066 is what happens when the only way
 * out of a wizard step is removed. The state is made honest instead: say what will happen,
 * and let the clinician choose it.
 */
export type UploadStepPrompt = 'none' | 'already-processed' | 'awaiting-new-documents';

export function getUploadStepPrompt(params: {
  /** null = first-time onboarding or a plain continuation, not a care-plan refresh. */
  refreshSource: CarePlanRefreshSource | null;
  documentsProcessed: boolean;
  /** Any file, pasted image or pasted text staged on this step right now. */
  hasStagedInput: boolean;
}): UploadStepPrompt {
  const { refreshSource, documentsProcessed, hasStagedInput } = params;
  // Something is staged: the normal "Process Documents" flow takes over, no prompt needed.
  if (hasStagedInput || !documentsProcessed) return 'none';
  return refreshSource === 'upload' ? 'awaiting-new-documents' : 'already-processed';
}

/**
 * The forward button's label on the Document Upload step. Only the 'upload'-with-nothing-staged
 * case changes: it now names the consequence instead of promising a review of data the
 * clinician believes they are about to replace.
 */
export function getUploadStepContinueLabel(prompt: UploadStepPrompt): string {
  return prompt === 'awaiting-new-documents'
    ? 'Continue without uploading'
    : 'Continue to Review';
}
